import _ from "lodash";
import { AbstractService } from "./AbstractService";
import { MailCollectionDTO, MailDTO } from "../types";
import axios from "axios";
import { ConfigurableInterface } from "./ConfigurableInterface";

export interface InboxServiceInterface extends ConfigurableInterface {
  createEmail(params: {
    phone: string;
    deleteIn?: number;
  }): Promise<{ status: number }>;
  getMails(params: { phone: string }): Promise<MailCollectionDTO>;
  getMail(params: { phone: string; timestamp: string }): Promise<MailDTO>;
  getMailAttachments(params: {
    phone: string;
    timestamp: string;
    attachment?: string;
  }): Promise<string | ArrayBuffer | ArrayBufferView | Blob>;
  getMailAttachmentsUrl(params: {
    phone: string;
    timestamp: string;
    attachment?: string;
  }): string;
  getMailImgUrl(params: {
    cid: string;
    phone: string;
    timestamp: string;
  }): string;
  deleteEmail(params: {
    phone: string;
    deleteIn?: number;
  }): Promise<{ status: number }>;
  sendMail(params: {
    to: string;
    subject: string;
    body: string;
    attachments?: Record<"content_type" | "filename" | "payload", string>[];
  }): Promise<{
    status: number;
    recipient: string;
    message_id: string;
  }>;
  forwardMail(params: {
    phone: string;
    timestamp: string;
    recipient: string;
  }): Promise<{
    status: number;
    message_id: string;
  }>;
  sendSms(params: {
    phone: string;
    message: string;
  }): Promise<{ status: number; recipient: string; message_id: string }>;
}

export class InboxService extends AbstractService
  implements InboxServiceInterface {
  public async createEmail(params: {
    phone: string;
    deleteIn?: number;
  }): Promise<{ status: number }> {
    this.checkConfig("inboxServiceUrl");
    const response = await axios.put(
      `${this.config.inboxServiceUrl}/${params.phone}/user`,
      {},
      // eslint-disable-next-line @typescript-eslint/camelcase
      params.deleteIn ? { params: { delete_in: params.deleteIn } } : {}
    );

    const status = (response.data as { status: number }).status;

    if (status !== 201) {
      throw new Error(`Server returned ${status} status code`);
    }

    return response.data;
  }

  public async getMails(params: { phone: string }): Promise<MailCollectionDTO> {
    this.checkConfig("inboxServiceUrl");
    return (
      await axios.get(`${this.config.inboxServiceUrl}/${params.phone}/emails`)
    ).data;
  }

  public async getMail(params: {
    phone: string;
    timestamp: string;
  }): Promise<MailDTO> {
    this.checkConfig("inboxServiceUrl");

    const data = (
      await axios.get(`${this.config.inboxServiceUrl}/${params.phone}/email`, {
        params: _.mapKeys(
          _.assign(
            {
              markRead: true,
              body: "html",
              attachments: true
            },
            _.omit(params, ["phone"])
          ),
          (v, k) => _.snakeCase(k)
        )
      })
    ).data;

    if (_.get(data, "status") === 500) {
      throw new Error(_.get(data, "message"));
    }

    return data;
  }

  public async getMailAttachments(params: {
    phone: string;
    timestamp: string;
    attachment?: string;
  }): Promise<string | ArrayBuffer | ArrayBufferView | Blob> {
    this.checkConfig("inboxServiceUrl");
    return await axios.get(this.getMailAttachmentsUrl(params), {
      responseType: "blob",
      params: _.omit(params, ["phone"])
    });
  }

  public getMailAttachmentsUrl(params: {
    phone: string;
    timestamp: string;
    attachment?: string;
  }): string {
    this.checkConfig("inboxServiceUrl");

    return axios.getUri({
      url: `${this.config.inboxServiceUrl}/${params.phone}/email_attachment`,
      params: _.omit(params, ["phone"])
    });
  }

  public getMailImgUrl(params: {
    cid: string;
    phone: string;
    timestamp: string;
  }): string {
    this.checkConfig("inboxServiceFileStorageUrl");

    return axios.getUri({
      url: `${this.config.inboxServiceFileStorageUrl}/${params.phone}/incoming/${params.timestamp}/${params.cid}`
    });
  }

  public async deleteEmail(params: {
    phone: string;
    deleteIn?: number;
  }): Promise<{ status: number }> {
    this.checkConfig("inboxServiceUrl");
    const response = await axios.delete(
      `${this.config.inboxServiceUrl}/${params.phone}/user`,
      // eslint-disable-next-line @typescript-eslint/camelcase
      { params: params.deleteIn ? { delete_in: params.deleteIn } : {} }
    );

    const status = (response.data as { status: number }).status;

    if (status !== 204) {
      throw new Error(`Server returned ${status} status code`);
    }

    return response.data;
  }

  public async sendMail(params: {
    to: string;
    subject: string;
    body: string;
    attachments?: Record<"content_type" | "filename" | "payload", string>[];
  }): Promise<{ status: number; recipient: string; message_id: string }> {
    this.checkConfig("inboxServiceUrl");

    const response = await axios.post(
      `${this.config.inboxServiceUrl}/email`,
      params
    );

    const status = (response.data as {
      status: number;
      recipient: string;
      message_id: string;
    }).status;

    if (status !== 202) {
      throw new Error(
        (response.data as { message?: string }).message ??
          `Server returned ${status} status code`
      );
    }

    return response.data;
  }

  public async forwardMail(params: {
    phone: string;
    timestamp: string;
    recipient: string;
  }): Promise<{
    status: number;
    message_id: string;
  }> {
    this.checkConfig("inboxServiceUrl");

    const response = await axios.post(
      `${this.config.inboxServiceUrl}/${params.phone}/email_forward`,
      { recipient: params.recipient },
      { params: { timestamp: params.timestamp } }
    );

    const status = (response.data as { status: number }).status;

    if (status !== 202) {
      throw new Error(
        (response.data as { message?: string }).message ??
          `Server returned ${status} status code`
      );
    }

    return response.data;
  }

  public async sendSms(params: {
    phone: string;
    message: string;
  }): Promise<{ status: number; recipient: string; message_id: string }> {
    this.checkConfig("inboxServiceUrl");

    const response = await axios.post(
      `${this.config.inboxServiceUrl}/${params.phone}/sms`,
      { message: params.message }
    );

    return response.data;
  }
}

export class StagingInboxService extends AbstractService
  implements InboxServiceInterface {
  public async createEmail(params: {
    phone: string;
    deleteIn?: number;
  }): Promise<{ status: number }> {
    this.checkConfig("stagingRequestDuration");

    return new Promise(resolve => {
      setTimeout(() => {
        resolve({ status: 201 });
      }, this.config.stagingRequestDuration);
    });
  }

  public async getMails(params: { phone: string }): Promise<MailCollectionDTO> {
    this.checkConfig("stagingRequestDuration");

    return new Promise(resolve => {
      setTimeout(() => {
        resolve([
          {
            from: ["Fake sender name", "fake@gmail.com"],
            subject: "Complex mail with multiple attachments and inline photo",
            timestamp: "2022-01-04T102256.774143",
            // eslint-disable-next-line @typescript-eslint/camelcase
            read_status: "True",
            // eslint-disable-next-line @typescript-eslint/camelcase
            attachments_count: 5
          },
          {
            from: ["", "no-reply@gmail.com"],
            subject: "Test mail from AWS SES",
            timestamp: "2022-01-04T102815.951306",
            // eslint-disable-next-line @typescript-eslint/camelcase
            read_status: "False",
            // eslint-disable-next-line @typescript-eslint/camelcase
            attachments_count: 0
          }
        ]);
      }, this.config.stagingRequestDuration);
    });
  }

  public async getMail(params: {
    phone: string;
    timestamp: string;
  }): Promise<MailDTO> {
    this.checkConfig("stagingRequestDuration");

    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          from: ["Fake sender name", "fake@gmail.com"],
          subject: "Complex email with multiple attachments and inline photo",
          timestamp: "2022-01-04T102256.774143",
          message: `Billy Bones is a fictional character appearing in the first section of Robert Louis Stevenson's 1883 novel Treasure Island. Among other things, he is notable for singing the "Dead Man's Chest" sea song. `,
          attachments: [
            {
              name: "1_image.png",
              size: 426700
            },
            {
              name: "cuic_overview.pdf",
              size: 2193615
            },
            {
              name: "first_graphics_program.mp4",
              size: 331439
            },
            {
              name: "image.png",
              size: 136413
            },
            {
              name: "mustang_ucce_setup.PNG",
              size: 96113
            }
          ]
        });
      }, this.config.stagingRequestDuration);
    });
  }

  public async getMailAttachments(params: {
    phone: string;
    timestamp: string;
    attachment?: string;
  }): Promise<string | ArrayBuffer | ArrayBufferView | Blob> {
    this.checkConfig("stagingRequestDuration");

    return new Promise(resolve => {
      setTimeout(() => {
        resolve(undefined);
      }, this.config.stagingRequestDuration);
    });
  }

  public getMailAttachmentsUrl(params: {
    phone: string;
    timestamp: string;
    attachment?: string;
  }): string {
    return "";
  }

  public getMailImgUrl(params: {
    cid: string;
    phone: string;
    timestamp: string;
  }): string {
    return "";
  }

  public async deleteEmail(params: {
    phone: string;
    deleteIn?: number;
  }): Promise<{ status: number }> {
    this.checkConfig("stagingRequestDuration");

    return new Promise(resolve => {
      setTimeout(() => {
        resolve({ status: 204 });
      }, this.config.stagingRequestDuration);
    });
  }

  public async sendMail(params: {
    to: string;
    subject: string;
    body: string;
    attachments?: Record<"content_type" | "filename" | "payload", string>[];
  }): Promise<{ status: number; recipient: string; message_id: string }> {
    this.checkConfig("stagingRequestDuration");

    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          status: 202,
          recipient: "recipient@email.com",
          // eslint-disable-next-line @typescript-eslint/camelcase
          message_id: "1de8df74-a9ec-466d-aab8-b490d3f0a595"
        });
      }, this.config.stagingRequestDuration);
    });
  }

  public async forwardMail(params: {
    phone: string;
    timestamp: string;
    recipient: string;
  }): Promise<{
    status: number;
    message_id: string;
  }> {
    this.checkConfig("stagingRequestDuration");

    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          status: 202,
          // eslint-disable-next-line @typescript-eslint/camelcase
          message_id:
            "0100017f54d72f5f-f87e2e5a-69fd-4889-9029-edd1e198b5fa-000000"
        });
      }, this.config.stagingRequestDuration);
    });
  }

  public async sendSms(params: {
    phone: string;
    message: string;
  }): Promise<{ status: number; recipient: string; message_id: string }> {
    this.checkConfig("stagingRequestDuration");

    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          status: 202,
          recipient: "+100500",
          // eslint-disable-next-line @typescript-eslint/camelcase
          message_id: "12b57fc0-9273-59e6-a73a-2548e3a88a19"
        });
      }, this.config.stagingRequestDuration);
    });
  }
}
